import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowUpFromLine, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, fmtQty } from "./stockUtils";

// Saída = sempre por produto, em cascata PVPS (vence primeiro, sai primeiro).
export function StockOutDialog({ product, onOpenChange, onDone }) {
  const open = !!product;
  const [qty, setQty] = useState("");
  const [reason, setReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setQty("");
      setReason("");
      setPreview(null);
    }
  }, [product]);

  useEffect(() => {
    if (!product || !qty || Number(qty) <= 0) {
      setPreview(null);
      return undefined;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post(`/sanitary/products/${product.id}/out`, { quantity: Number(qty), preview: true });
        setPreview(data);
      } catch {
        setPreview(null);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [product, qty]);

  const submit = async () => {
    if (!qty || Number(qty) <= 0) {
      toast.error("Quantidade deve ser maior que 0");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/sanitary/products/${product.id}/out`, { quantity: Number(qty), reason });
      toast.success(`Saída de ${fmtQty(qty)} ${product.unit} em ${data.movements.length} lote(s) — saldo ${fmtQty(data.new_total)} ${product.unit}`);
      onOpenChange(false);
      onDone?.(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao registrar saída");
    } finally {
      setSaving(false);
    }
  };

  const enough = preview ? preview.enough : true;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onOpenChange(false)}>
      <DialogContent data-testid="stock-out-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <ArrowUpFromLine className="h-5 w-5 text-rose-700" /> Saída — {product?.name}
          </DialogTitle>
          <DialogDescription className="font-body text-xs text-stone-500">
            Informe só a quantidade: o sistema baixa automaticamente dos lotes que vencem primeiro (PVPS).
            Disponível: <b>{fmtQty(product?.quantity)} {product?.unit}</b> em {product?.lots_count || 0} lote(s).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Quantidade ({product?.unit}) *</Label>
            <Input type="number" step="0.01" min="0" value={qty} onChange={(e) => setQty(e.target.value)} className="mt-1 bg-white" data-testid="stock-out-qty" autoFocus />
          </div>
          {preview && (
            <div className={`rounded-md border p-3 space-y-1.5 ${enough ? "border-emerald-200 bg-emerald-50/50" : "border-rose-300 bg-rose-50"}`} data-testid="stock-out-preview">
              <div className="text-xs font-body font-semibold text-stone-800">
                {enough ? "Baixa PVPS — lotes que serão consumidos:" : `Saldo insuficiente — disponível ${fmtQty(preview.total)} ${preview.unit}`}
              </div>
              {preview.plan.map((step, i) => (
                <div key={step.lot_id} className="flex items-center justify-between gap-2 text-xs font-body" data-testid={`stock-out-step-${i}`}>
                  <span className="flex items-center gap-1.5 min-w-0">
                    <Badge className="bg-white border border-stone-300 text-stone-700 text-[10px] shrink-0">{i + 1}º</Badge>
                    <span className="truncate">
                      {step.batch ? `Lote ${step.batch}` : "Lote s/ nº"} · val. {fmtDate(step.expires_at)}
                    </span>
                  </span>
                  <span className="tabular-nums shrink-0 flex items-center gap-1">
                    <b className="text-rose-700">−{fmtQty(step.take)}</b>
                    <ArrowRight className="h-3 w-3 text-stone-400" />
                    <span className="text-stone-500">resta {fmtQty(step.remaining)}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
          <div>
            <Label>Motivo</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Consumo no turno, perda, transferência…" className="mt-1 bg-white" data-testid="stock-out-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving || !enough} className="bg-rose-700 hover:bg-rose-800 text-white" data-testid="stock-out-submit">
            Registrar saída
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
